/**
 * Escopo multi-empresa da sessão.
 *
 * O backend confia no filtro enviado pelo front: um filtro sem `account` devolve
 * a base inteira. Toda leitura de lista/dropdown precisa passar por aqui para
 * não vazar dados de outras contas.
 *
 * Regra: SUPER_ADMIN_MASTER tem escopo global; GROUP_ADMIN só pode alternar
 * entre as contas assinadas no seu CompanyGroup. Os demais perfis enxergam a
 * própria conta e, quando aplicável, o próprio cliente. Sem escopo, falha fechado.
 */
const GLOBAL_SCOPE_SUBTYPE = 'SUPER_ADMIN_MASTER'
const GROUP_SCOPE_SUBTYPE = 'GROUP_ADMIN'

export const getSessionUser = state => state?.$session?.get('user') || {}

export const getSessionSubtype = state => String(getSessionUser(state)?.companyUser?.subtype || '').toUpperCase()

export const hasGlobalScope = state => getSessionSubtype(state) === GLOBAL_SCOPE_SUBTYPE

export const getSessionGroupAccountIds = state => {
    if (getSessionSubtype(state) !== GROUP_SCOPE_SUBTYPE) return []
    const accounts = getSessionUser(state)?.companyGroup?.accounts
    if (!Array.isArray(accounts)) return []
    return accounts.map(account => (typeof account === 'object' ? account?._id : account)).filter(Boolean)
}

export const getSessionAccount = state => {
    const account = getSessionUser(state)?.account
    return account?._id ? account : null
}

export const getSessionAccountId = state => getSessionAccount(state)?._id || ''

export const getSessionClient = state => {
    const client = getSessionUser(state)?.client
    return client?.type === 'CLIENT' && client?._id ? client : null
}

export const getSessionClientId = state => getSessionClient(state)?._id || ''

// Um id pedido só sobrevive se for o da própria sessão; qualquer outro vira vazio.
export const scopeAccountId = (state, account) => {
    if (hasGlobalScope(state)) return account || ''

    const groupAccounts = getSessionGroupAccountIds(state)
    if (groupAccounts.length > 0) {
        if (!account) return ''
        return groupAccounts.includes(account) ? account : ''
    }

    const sessionAccount = getSessionAccountId(state)
    if (!sessionAccount) return ''
    if (account && account !== sessionAccount) return ''

    return sessionAccount
}

export const scopeClientId = (state, client) => {
    if (hasGlobalScope(state)) return client || ''

    const sessionClient = getSessionClientId(state)
    if (!sessionClient) return client || ''
    if (client && client !== sessionClient) return ''

    return sessionClient
}

/**
 * Injeta o escopo da sessão no objeto de filtros, no lugar, antes do request.
 * Retorna `false` quando o escopo não pôde ser resolvido — nesse caso a tela
 * deve abortar a consulta em vez de disparar um filtro aberto.
 */
export const applySessionScope = (state, filters) => {
    if (!filters) return false
    if (hasGlobalScope(state)) return true

    const groupAccounts = getSessionGroupAccountIds(state)
    if (groupAccounts.length > 0) {
        if (filters.account) return groupAccounts.includes(filters.account)
        filters.accounts = groupAccounts
        return true
    }

    const account = getSessionAccountId(state)
    if (!account) return false

    filters.account = account

    // Só reforça o cliente em telas que já filtram por cliente; injetar a chave
    // em filtros que não a suportam mudaria a semântica da consulta.
    const client = getSessionClientId(state)
    if (client && 'client' in filters) filters.client = client

    return true
}
